import { inject, Injectable } from "@angular/core";
import { BaseJob, JobType, MessageResponseEnum, NonServicePeriodTypeEnum, Route, RouteUpdate, ScheduledFlagStop, StopTypeEnum } from "../types";
import { environment } from "src/environments/environment";
import { AppData } from "../data";
import { UtilityService } from "./utility/utility.service";
import { Subject } from "rxjs";
import { TranslateService } from "./translate.service";
import { getStopTypeLabel } from "../helpers/ui-helpers";
import { DateTime } from "luxon";

@Injectable({ providedIn: 'root' })
export class RouteSetupService {

  private readonly _data = inject(AppData);
  private readonly _translateService = inject(TranslateService);
  private readonly _utilityService = inject(UtilityService);

  private readonly _onUserLoggedOut$ = new Subject<boolean>();
  public readonly onUserLoggedOut$ = this._onUserLoggedOut$.asObservable();

  public async routeSetup(routeUpdate: RouteUpdate): Promise<RouteUpdate> {
    const { route, messages } = routeUpdate;

    if (!route?.routeNumber || !route?.id) { return { route: this._data.route, noShowOptions: this._data.noShowOptions, messages: [] }; }

    environment.features.hasOnTheWay = !!route.allowOnTheWay;
    // Normalize Data
    route.jobs = (route?.jobs || []).map(job => ({
      ...job,
      jobType: this._utilityService.jobTypeToInt(job.jobType),
    }));

    // handle scheduledFlagStops
    // sort the flagStops
    if (route.scheduledFlagStops?.length && route.fixedRoutesEnabled) {
      route.scheduledFlagStops?.sort((o1: ScheduledFlagStop, o2: ScheduledFlagStop) => {
        return (o1.departureTime > o2.departureTime) ? 1 : ((o1.departureTime < o2.departureTime) ? -1 : 0);
      });
    } else if (route.fixedRoutesEnabled && route.id === this._data.route.id) {
      //  because if flag stops have not changed, comes in as empty []
      route.scheduledFlagStops = this._data.scheduledFlagStops;
    } else {
      route.scheduledFlagStops = [];
    }

    // handle nonServicePeriods

    if (route.break && !route.break.hasBeenCompleted) {
      route.break = { ...route.break, nonServicePeriodType: NonServicePeriodTypeEnum.Break };
    }

    //  Credit Card payments
    route.creditCardMin = route.creditCardMin ?? 2;
    route.creditCardMax = route.creditCardMax ?? 200;

    //  dispatcher messages
    const _previousMessages = this._data.messages || [];
    let _messages = (messages || []).map(message => ({ ...message, createdDate: new Date(message.createdDate), messageResponseType: Number(message.messageResponseType) }));
    const priorityMessages = _messages.filter(m => m.messageResponseType === MessageResponseEnum.Acknowledge && !_previousMessages.find(pm => pm.id === m.id));
    const { added, removed, changed } = this.changedJobs(this._data.route, route);
    const notifications = this.getUpdateNotifications(this._data.route, route, { added, removed, changed });

    //  if driving mode is enabled and we are currently on a route, we hold the update until driving mode is disabled
    if (this._data.drivingMode && this._data.route?.id) {
      //  determine if the next job is affected / changed
      const nextJob = this._data.route.jobs?.find(j => !j.completed);
      const nextJobChanged = removed.find(j => j.rideId === nextJob.rideId || j.flagStopId === nextJob.flagStopId || (j.stopId && j.stopId === nextJob.stopId))
      || changed.find(j => j.rideId === nextJob.rideId || j.flagStopId === nextJob.flagStopId || (j.stopId && j.stopId === nextJob.stopId));
      //  handle dispatch messages
      if (environment.features.messagesRetain) { _messages = [ ...(this._data.heldRouteUpdate?.messages || []), ..._messages ]; }
      const hasUrgentMessage = _messages.filter(m => m.messageResponseType === MessageResponseEnum.Acknowledge).length > 0;
      const quantity = added.length + removed.length + changed.length + _messages.length;
      this._data.set('drivingModeNotifications', {
        quantity,
        isUrgent: hasUrgentMessage || nextJobChanged,
      });
      if (quantity) { this._utilityService.playBeep(); }
      const _routeUpdate: RouteUpdate = { ...routeUpdate, route: structuredClone(route), messages: structuredClone(_messages) };
      this._data.set('heldRouteUpdate', _routeUpdate);
      this._data.set('messages', _messages);
      return { ..._routeUpdate, route };
    }

    //  no longer in driving mode so clean up driving mode state
    this._data.set('drivingModeNotifications', { quantity: 0, isUrgent: false });
    //  update app state with new route information
    this._data.set('routeNotifications', notifications);
    this._data.set('route', structuredClone(route));
    this._data.set('messages', _messages);
    this._data.set('priorityMessages', priorityMessages);

    return { ...routeUpdate, route, messages: structuredClone(_messages) };
  }

  private changedJobs(oldRoute: Route, newRoute: Route) {
    if (!oldRoute?.jobs || !newRoute?.jobs) { return { added: [], removed: [], changed: [] }; }

    // Create update object for added, changed, removed jobs
    return newRoute.jobs.reduce((acc, current) => {

      const previous = oldRoute.jobs?.find(previous =>
        previous.stopId === current.stopId
        && previous.stopType === current.stopType
        && previous.rideId === current.rideId
        && previous.jobType == current.jobType
        // CTS: normalize because when marking a ride as will call ready in TripMaster, the flagstop ID is changed from being an empty string to null
        && (previous.flagStopId ? previous.flagStopId : null) === (current.flagStopId ? current.flagStopId : null),
      );

      if (previous?.completed) { return acc; }

      //  the only properties we notify of changes are location and scheduled time
      const isIdentical = previous
      ? previous.scheduledTime === current.scheduledTime && JSON.stringify(previous.location) === JSON.stringify(current.location)
      : false;

      //  Job has not changed or was completed already
      if (previous && (isIdentical || previous.completed)) { return acc; }

      //  Job is a pull-out and a pull-out has already been preformed
      if (
        current.stopType === StopTypeEnum.PULL_OUT
        && oldRoute.jobs?.find(j => j.stopType == StopTypeEnum.PULL_OUT)?.completed
      ) {
        return acc;
      }

      //  Job existed and has changed
      if (previous) {
        return { ...acc, changed: [...acc.changed, { previous, current }] };
      }

      //  Job is new
      return { ...acc, added: [...acc.added, current] };
    }, {
      added: [],
      //  Job does not exist in new array
      removed: oldRoute.jobs.filter(previous => {
        //  ignoring anything that was already completed
        if (previous.completed) { return false; }
        return !newRoute.jobs?.find(current =>
             previous.stopId     === current.stopId
          && previous.stopType   === current.stopType
          && previous.rideId     === current.rideId
          && previous.jobType    == current.jobType
          && previous.flagStopId === current.flagStopId,
        );
      }),
      changed: [],
    });
  }

  /** Handle getting the notification to tell driver what has changed when the route updated */
  private getUpdateNotifications(oldRoute: Route, newRoute: Route, updates): string[] {
    if (!oldRoute?.jobs || !newRoute?.jobs) { return []; }
    //  Create array of notifications

    const added = updates.added.reduce((acc, job: BaseJob) => {
      const unscheduled = this._translateService.translate('LABEL.unscheduled');
      const riderName = this._utilityService.handleNameForNotification(job.riderFirstName, job.riderLastName);

      // If dropOff ignore, we already dealt with this on the pickup
      if (job.jobType == JobType.DropOff) { return acc; }

      const dateTime = job.scheduledTime ? DateTime.fromISO(job.scheduledTime as any) : null;
      const jobDateString = dateTime && dateTime.toFormat('HH:mm') !== '00:00' ? dateTime.toFormat('HH:mm') : unscheduled;

      let message = '';
      if (!job.stopType || job.stopType === StopTypeEnum.JOB) {
        message = `${this._translateService.translate('LABEL.added')} ${this._translateService.translate('LABEL.pickupAndDropoff')} ${this._translateService.translate('LABEL.for')} ${riderName} ${this._translateService.translate('LABEL.at')} ${jobDateString} `;
      } else {
        message = `${this._translateService.translate('LABEL.added')} ${ getStopTypeLabel(job.stopType, this._translateService) } ${ this._translateService.translate('LABEL.at') } ${ jobDateString } `;
      }

      return [...acc, message];
    }, []);

    const removed = updates.removed.reduce((acc, job: BaseJob) => {
      const unscheduled = this._translateService.translate('LABEL.unscheduled');
      const riderName = this._utilityService.handleNameForNotification(job.riderFirstName, job.riderLastName);

      // If dropOff ignore, we already dealt with this on the pickup
      if (job.jobType == JobType.DropOff) { return acc; }

      const dateTime = job.scheduledTime ? DateTime.fromISO(job.scheduledTime as any) : null;
      const jobDateString = dateTime && dateTime.toFormat('HH:mm') !== '00:00' ? dateTime.toFormat('HH:mm') : unscheduled;

      let message = '';
      if (!job.stopType || job.stopType === StopTypeEnum.JOB) {
        message = `${ this._translateService.translate('LABEL.removed') } ${ this._translateService.translate('LABEL.pickupAndDropoff') } ${ this._translateService.translate('LABEL.for') } ${ riderName } ${ this._translateService.translate('LABEL.at') } ${ jobDateString }`;
      } else {
        message = `${ this._translateService.translate('LABEL.removed') } ${ getStopTypeLabel(job.stopType, this._translateService) } ${ this._translateService.translate('LABEL.at') } ${ jobDateString } `;
      }
      return [...acc, message];
    }, []);

    const changed = updates.changed.reduce((acc, { previous, current }: { previous: BaseJob, current: BaseJob }) => {
      const riderName = this._utilityService.handleNameForNotification(current.riderFirstName, current.riderLastName);
      const jobTypeString = current.jobType == JobType.DropOff ? this._translateService.translate('LABEL.dropoffNoSpace') : this._translateService.translate('LABEL.pickup');
      const stopType = current.stopType;
      const baseMessage = (!stopType || stopType === StopTypeEnum.JOB) ? `${jobTypeString} ${riderName}` : getStopTypeLabel(stopType, this._translateService);

      //  Handling a change in scheduled time
      if (previous.scheduledTime !== current.scheduledTime) {
        let message = '';

        const previousDateTime = previous.scheduledTime ? DateTime.fromISO(previous.scheduledTime as any) : null;
        const currentDateTime = current.scheduledTime ? DateTime.fromISO(current.scheduledTime as any) : null;

        const unscheduled = this._translateService.translate('LABEL.unscheduled');
        const previousTime = previousDateTime && previousDateTime.toFormat('HH:mm') !== '00:00' ? previousDateTime.toFormat('HH:mm') : unscheduled;
        const currentTime = currentDateTime && currentDateTime.toFormat('HH:mm') !== '00:00' ? currentDateTime.toFormat('HH:mm') : unscheduled;

        //  this account for going from null to 00:00 in which case the time is not actually changed even though the data did
        if (previousTime === currentTime) { return acc; }

        const timeDiff = previousTime === unscheduled || currentTime === unscheduled
          ? Infinity
          : Math.abs(previousDateTime.diff(currentDateTime, 'minutes').minutes);

        //  only notify if time change is greater than 5 minutes
        if (timeDiff > 5) {
          message = `${ baseMessage } ${this._translateService.translate('LABEL.hasChangedFrom')} ${previousTime} ${this._translateService.translate('LABEL.to')} ${currentTime}`;
          acc = [...acc, message];
        };

        return acc;
      }

      //  Handling a location change
      if (JSON.stringify(previous.location) !== JSON.stringify(current.location)) {
        const oldLocation = `${previous.location.address} ${previous.location.city} ${previous.location.state} ${previous.location.zipCode}`;
        const newLocation = `${current.location.address} ${current.location.city} ${current.location.state} ${current.location.zipCode}`;
        const message = `${ baseMessage } ${this._translateService.translate('LABEL.addrChangedFrom')} `
          + `${oldLocation} ${this._translateService.translate('LABEL.to')} ${newLocation} `;
        acc = [...acc, message];
      }

      return acc;

    }, []);

    //  make 1 notification out of all messages
    //  new Set to filter out any duplicates
    const messages = Array.from(new Set([...removed, ...added, ...changed ])).reduce((acc, message) => `${acc}${acc.length ? '\n' : ''}${message}`, '');
    //  don't send an a empty message
    return messages.length ? [messages] : [];
  }

}
